import { Sentiment } from '@mnd-frontend/graphql-schema';
import { useClickOutside } from '@mnd-frontend/hooks';
import {
  DANGER_700,
  GREY_200,
  GREY_400,
  GREY_800,
  GREY_900,
  GREY_1000,
  Icons,
  SHADOW_2,
  SHADOW_3,
} from '@mnd-frontend/ui';
import { type ReactElement, useCallback, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { ReactComponent as SentimentNegative } from '../../assets/sentiment-negative.svg';
import { ReactComponent as SentimentNeutral } from '../../assets/sentiment-neutral.svg';
import { ReactComponent as SentimentPositive } from '../../assets/sentiment-positive.svg';
import { useReportTranslation } from '../../hooks';
import type { TranslationFunction } from '../../types';

const SentimentWrapper = styled.div<{ $disabled?: boolean }>`
  position: relative;
  display: inline-block;
  svg {
    width: 1rem;
    height: 1rem;
  }

  ${props =>
    props.$disabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}
`;

const SentimentPopup = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  min-width: 100%;
  z-index: 50;
  background: white;
  transform: translateY(100%);
  box-shadow: ${SHADOW_3};
  border-radius: 0.25rem;
  overflow: hidden;
`;

const SentimentButton = styled.button`
  background: none;
  border: 0;
  display: block;
  padding: 0;
  font-size: 0.875rem;
  width: 100%;
  color: ${GREY_900};
  &:hover {
    background: ${GREY_200};
  }
  &:not(:first-child) {
    border-top: 1px solid ${GREY_400};
  }
  > span {
    pointer-events: none;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem 0.25rem;
    width: 100%;
    gap: 0.5rem;
  }
`;

export const sentimentColor = ({ $sentiment }: { $sentiment?: Sentiment }) =>
  $sentiment === Sentiment.Positive
    ? '#D0F2D8' // TODO: check this color
    : $sentiment === Sentiment.Negative
      ? DANGER_700
      : GREY_800;

export const sentimentColorOpacity = ({ $sentiment }: { $sentiment?: Sentiment }) =>
  $sentiment === Sentiment.Positive ? 'FF' : 29;

const sentimentChipStyles = css<{ $sentiment?: Sentiment }>`
  border: 0;
  border-radius: 0.25rem;
  svg {
    color: ${GREY_1000};
  }
  ${Icons.ChevronDown} {
    height: ${({ theme }) => theme.sizingIconXxs};
    width: ${({ theme }) => theme.sizingIconXxs};
  }
  padding: 0.5rem;
  color: ${GREY_900};
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 0;
  background: ${sentimentColor}${sentimentColorOpacity};
  > span {
    display: flex;
    height: 100%;
    line-height: 1.2;
    align-items: center;
    gap: 0.25rem;
  }
`;
const SentimentDropdownButton = styled.button<{ $sentiment?: Sentiment }>`
  &:hover {
    box-shadow: ${SHADOW_2};
  }
  > * {
    pointer-events: none;
  }
  ${sentimentChipStyles}
`;

const SentimentChip = styled.div<{ $sentiment?: Sentiment }>`
  ${sentimentChipStyles}
`;

export const sentimentLabel = (
  t: TranslationFunction,
  { sentiment, count = 1 }: { sentiment?: Sentiment; count?: number },
): string => {
  switch (sentiment) {
    case Sentiment.Positive:
      return t('reports_sentiment-positive', { count });
    case Sentiment.Neutral:
      return t('reports_sentiment-neutral', { count });
    case Sentiment.Negative:
      return t('reports_sentiment-negative', { count });
    default:
      return 'N/A';
  }
};

export const sentimentIcon = (sentiment: Sentiment): ReactElement => {
  switch (sentiment) {
    case Sentiment.Positive:
      return <SentimentPositive />;
    case Sentiment.Neutral:
      return <SentimentNeutral />;
    case Sentiment.Negative:
      return <SentimentNegative />;
  }
};

export const SentimentDropdown = ({
  value,
  skipLabel,
  changeSentiment,
  disabled = false,
}: {
  changeSentiment?: (sentiment: Sentiment) => void;
  skipLabel?: boolean;
  value?: Sentiment;
  disabled?: boolean;
}) => {
  const { t } = useReportTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useClickOutside(
    wrapperRef,
    () => {
      setDropdownOpen(false);
    },
    dropdownOpen,
  );
  const toggle = useCallback(() => setDropdownOpen(o => !o), []);

  return (
    <SentimentWrapper ref={wrapperRef} $disabled={disabled}>
      {changeSentiment ? (
        <SentimentDropdownButton $sentiment={value} type="button" onClick={toggle}>
          <span>
            {value && sentimentIcon(value)}
            {!skipLabel && <span>{sentimentLabel(t, { sentiment: value })}</span>}
            {skipLabel && !value && <span>N/A</span>}
            <Icons.ChevronDown />
          </span>
        </SentimentDropdownButton>
      ) : (
        <SentimentChip $sentiment={value}>
          <span>
            {value && sentimentIcon(value)}
            {!skipLabel && <span>{sentimentLabel(t, { sentiment: value })}</span>}
            {skipLabel && !value && <span>N/A</span>}
          </span>
        </SentimentChip>
      )}
      {dropdownOpen && changeSentiment && (
        <SentimentPopup>
          <SentimentButton
            type="button"
            onClick={() => {
              changeSentiment(Sentiment.Positive);
              setDropdownOpen(false);
            }}
          >
            <span>
              {sentimentIcon(Sentiment.Positive)}
              <span>{sentimentLabel(t, { sentiment: Sentiment.Positive })}</span>
            </span>
          </SentimentButton>
          <SentimentButton
            type="button"
            onClick={() => {
              changeSentiment(Sentiment.Neutral);
              setDropdownOpen(false);
            }}
          >
            <span>
              {sentimentIcon(Sentiment.Neutral)}
              <span>{sentimentLabel(t, { sentiment: Sentiment.Neutral })}</span>
            </span>
          </SentimentButton>
          <SentimentButton
            type="button"
            onClick={() => {
              changeSentiment(Sentiment.Negative);
              setDropdownOpen(false);
            }}
          >
            <span>
              {sentimentIcon(Sentiment.Negative)}
              <span>{sentimentLabel(t, { sentiment: Sentiment.Negative })}</span>
            </span>
          </SentimentButton>
        </SentimentPopup>
      )}
    </SentimentWrapper>
  );
};
